import React from 'react';
import { motion } from 'framer-motion';
import IndustryPageHeading from '../components/IndustryPageHeading';
import EnterpriseVideo from '../components/EnterpriseVideo';

/**
 * Finance Solutions — internal route /solutions/industries/finance
 *
 * Three content sections with alternating layout direction (text/video),
 * mirroring the first three sections of the Logistics Solutions page and
 * matching the Insurance / Real Estate / Retail / Healthcare / Manufacturing
 * industry subpages. Each section embeds a Cloudflare Stream video using
 * the same video element + play/pause + volume control pattern used
 * elsewhere on the site (ProductA / LogisticsSolutions), with per-video
 * poster thumbnails pulled at a specific time. After the three sections
 * the page goes directly to the site's standard footer, which is rendered
 * globally by Layout.
 */

const VideoDemoFrame = ({ cloudflareVideoId, posterTime }) => (
  <div className="w-full">
    <EnterpriseVideo
      cloudflareVideoId={cloudflareVideoId}
      posterTime={posterTime}
      title="Finance demo video"
      testId={`finance-industry-video-${cloudflareVideoId}`}
      className="w-full rounded-lg bg-gradient-to-br from-[#E8E4DC] to-[#E8EDE7]"
      style={{
        aspectRatio: '16 / 9',
        border: '2px solid #D4AF37',
        boxShadow:
          'inset 0 2px 8px rgba(20, 30, 48, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)',
      }}
    />
  </div>
);

const FeatureBlock = ({ title, description }) => (
  <div className="pl-4 relative">
    <div
      className="absolute left-0 top-1 bottom-1 w-0.5 rounded-full"
      style={{
        background: 'linear-gradient(to bottom, #D4AF37, rgba(212, 175, 55, 0.4))',
      }}
    />
    <h3 className="text-base sm:text-lg font-medium text-black/90">{title}</h3>
    <p className="text-sm text-black/70 mt-1">{description}</p>
  </div>
);

const TextContent = ({ eyebrow, heading, body, features, isPageHeading }) => {
  const HeadingTag = isPageHeading ? 'h1' : 'h2';
  return (
    <div>
      <p className="text-sm md:text-base font-bold text-black/90 mb-3 md:mb-4 uppercase tracking-wider">
        {eyebrow}
      </p>
      <HeadingTag className="text-3xl md:text-4xl font-medium text-black/90 mb-4 md:mb-6">
        {heading}
      </HeadingTag>
      <p className="text-base sm:text-lg text-black/70 mb-8 md:mb-10">{body}</p>
      <div className="space-y-6">
        {features.map((f) => (
          <FeatureBlock key={f.title} title={f.title} description={f.description} />
        ))}
      </div>
    </div>
  );
};

const sections = [
  {
    eyebrow: 'Financial Operations & Bookkeeping',
    cloudflareVideoId: '5dd832746c02a43f447b5a6c391e51f6',
    posterTime: 5,
    heading: 'Books that close themselves.',
    body:
      'GoogLyn automates bookkeeping, expense tracking, and reconciliation \u2014 so finance teams close the books in hours, not days, and never chase a missing receipt.',
    features: [
      {
        title: 'Automated bookkeeping',
        description: 'Transactions categorized and recorded in real time.',
      },
      {
        title: 'Expense management',
        description: 'Approvals and tracking handled without manual review.',
      },
      {
        title: 'Smart forecasting',
        description: 'Predictive insights built from live financial data.',
      },
    ],
  },
  {
    eyebrow: 'Client & Transaction Support',
    cloudflareVideoId: '889576b2f94baac584e5c629eaf8a45d',
    posterTime: 5,
    heading: 'Every client question and invoice, handled instantly.',
    body:
      'GoogLyn answers client questions, processes payments, and generates invoices automatically \u2014 reducing the back-and-forth that slows down every finance team.',
    features: [
      {
        title: '24/7 client support',
        description: 'Account and billing questions answered instantly, any time of day.',
      },
      {
        title: 'Automated payment processing',
        description: 'Reconciliation handled without manual entry.',
      },
      {
        title: 'Instant invoice generation',
        description: 'Invoices created and delivered without a finance rep touching a keyboard.',
      },
    ],
  },
  {
    eyebrow: 'Compliance & Reporting',
    cloudflareVideoId: '507c38ccbe76246767063b07abd8a460',
    posterTime: 5,
    heading: 'Every number, always audit-ready.',
    body:
      'Compliance monitoring, fraud detection, and reporting run continuously in the background, so finance teams work from real-time data instead of quarterly reconstructions.',
    features: [
      {
        title: 'Continuous compliance monitoring',
        description: 'Audit trails stay current automatically.',
      },
      {
        title: 'Real-time reporting',
        description: 'Financial data always at your fingertips.',
      },
      {
        title: 'Fraud detection',
        description: 'Anomalies flagged before they become losses.',
      },
    ],
  },
];

const IndustryFinance = () => {
  return (
    <div className="w-full" data-testid="finance-industry-page">
      <IndustryPageHeading name="Finance" testId="finance-industry-page-heading" />
      {sections.map((section, idx) => {
        const textFirst = idx % 2 === 0; // sections 1,3 → text left (video right); 2 → video left
        const bg = idx % 2 === 0 ? 'bg-white' : 'bg-[#F5F7F5]';
        return (
          <section
            key={section.eyebrow}
            className={`py-16 md:py-24 ${bg}`}
            data-testid={`finance-industry-section-${idx + 1}`}
          >
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center"
              >
                {textFirst ? (
                  <>
                    <TextContent {...section} isPageHeading={idx === 0} />
                    <VideoDemoFrame
                      cloudflareVideoId={section.cloudflareVideoId}
                      posterTime={section.posterTime}
                    />
                  </>
                ) : (
                  <>
                    {/* Video first on desktop; text stays above video on mobile for readability */}
                    <div className="order-2 lg:order-1">
                      <VideoDemoFrame
                        cloudflareVideoId={section.cloudflareVideoId}
                        posterTime={section.posterTime}
                      />
                    </div>
                    <div className="order-1 lg:order-2">
                      <TextContent {...section} isPageHeading={idx === 0} />
                    </div>
                  </>
                )}
              </motion.div>
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default IndustryFinance;
